import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'

const t = messages.mcpConsent

/**
 * Claude bağlantısı onayı (docs/MCP-PLAN.md K8): Claude `/authorize`'dan buraya yönlendirir,
 * kullanıcı kendi oturumuyla onaylar, backend kodu üretir ve tarayıcı Claude'a döner.
 * Yetki kararı backend'de (`ai.use`); buradaki kontrol yalnız düğmeyi gizler.
 */
export function McpConsentPage() {
  const [params] = useSearchParams()
  const request = params.get('request') ?? ''
  const { user } = useAuth()

  const info = useQuery({
    queryKey: queryKeys.mcpConsent(request),
    queryFn: () => services.mcpConsent.info(request),
    enabled: request !== '',
    retry: false,
  })
  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      approve ? services.mcpConsent.approve(request) : services.mcpConsent.deny(request),
    onSuccess: (redirectUrl) => window.location.assign(redirectUrl),
  })

  return (
    <div className="bg-muted flex min-h-dvh items-center justify-center p-4">
      <div className="border-border bg-card text-card-foreground w-full max-w-md rounded-md border p-6">
        <h1 className="text-lg font-semibold">{t.title}</h1>
        {request === '' ? (
          <p className="text-muted-foreground mt-2 text-sm">{t.missingRequest}</p>
        ) : info.isLoading ? (
          <Loading rows={3} />
        ) : info.isError ? (
          <ErrorState error={info.error} />
        ) : (
          info.data && (
            <div className="mt-2 flex flex-col gap-4 text-sm">
              <p>{t.request(info.data.clientName)}</p>
              {user && <p className="text-muted-foreground">{t.signedInAs(user.email)}</p>}
              {info.data.hasPermission ? (
                <div className="flex flex-col gap-1">
                  <p className="font-medium">{t.canTitle}</p>
                  <ul className="text-muted-foreground list-disc pl-5">
                    <li>{t.canViews}</li>
                    <li>{t.canQuery}</li>
                    <li>{t.cannotWrite}</li>
                  </ul>
                  <p className="text-muted-foreground mt-1 text-xs">{t.revokeHint}</p>
                </div>
              ) : (
                <p role="alert" className="text-destructive">
                  {t.noPermission}
                </p>
              )}
              {decide.isError && <ErrorState error={decide.error} />}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate(false)}
                >
                  {t.deny}
                </Button>
                {info.data.hasPermission && (
                  <Button disabled={decide.isPending} onClick={() => decide.mutate(true)}>
                    {decide.isPending ? t.approving : t.approve}
                  </Button>
                )}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}
